// 产品矩阵系统 - 产品管理页面 (模块化重构版)
// v4.2.0 - 性能优化：移除 hook 级联依赖
// 参考 Apple 设计模式：状态扁平化，避免 hook 间依赖
import React, { useEffect, useState, useMemo, useCallback, useRef, useTransition } from 'react';
import {
  Plus,
  Search,
  Star,
  X,
  Grid3X3,
  List,
  Copy,
  Settings2,
  Package,
  GitBranch,
  ArrowUp,
  ArrowDown,
  LayoutGrid,
  Trash2,
  Edit2,
} from 'lucide-react';
import { useProductStore } from '@/poster/stores/product-store';
import { supabase } from '@/config/supabase';
import type { Product, ProductModel, ProductCategory } from '@/poster/types';
import { ProductCategoryLabels } from '@/poster/types';

// 导入模块化组件
import {
  ProductCard,
  ProductGridCard,
  ProductDetailPanel,
  ProductFormModal,
  ModelFormModal,
  SeriesManagerModal,
  HierarchyView,
  ConfirmDialog,
} from './components';

// 导入 hooks（只用不会造成级联渲染的）
import { useProductCopy } from './hooks';

// 导入常量
import { categoryOptions, sortOptions } from './constants';

// 导入类型
import type {
  SortField,
  SortOrder,
  ViewMode,
  PageMode,
  ConfirmDialogState,
  PanelEditingModel,
} from './types';

// 系列类型
interface ProductSeries {
  id: string;
  name: string;
  name_en?: string;
  category: string;
  description?: string;
  image_url?: string;
  is_active: boolean;
  sort_order?: number;
  product_count?: number;
}

// 导航状态类型
type NavigationLevel = 'root' | 'category' | 'series' | 'product';

interface NavigationState {
  level: NavigationLevel;
  category?: ProductCategory;
  series?: ProductSeries;
  product?: Product;
}

// =====================================================
// 主组件
// =====================================================

export function ProductManagePage() {
  const {
    products,
    isLoading,
    categoryStats,
    fetchProducts,
    fetchCategoryStats,
    createProduct,
    updateProduct,
    deleteProduct,
    createProductModel,
    updateProductModel,
    deleteProductModel,
  } = useProductStore();

  // 产品复制 hook
  const { copyProduct } = useProductCopy();
  
  // 追踪是否是首次加载
  const isFirstLoad = useRef(true);

  // ===== React 18 并发特性：标签切换使用 transition =====
  // 这让切换操作变成"非紧急"更新，UI 保持响应
  const [isPending, startTransition] = useTransition();

  // ===== 核心状态（扁平化，不依赖其他 hook）=====
  const [seriesList, setSeriesList] = useState<ProductSeries[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [editingModel, setEditingModel] = useState<{ productId: string; model: ProductModel | null } | null>(null);
  const [selectedSeries, setSelectedSeries] = useState<string>('all');
  const [showSeriesManager, setShowSeriesManager] = useState(false);
  const [pageMode, setPageMode] = useState<PageMode>('products');
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [detailProduct, setDetailProduct] = useState<Product | null>(null);
  const [panelEditingModel, setPanelEditingModel] = useState<PanelEditingModel>(null);

  // 层级导航状态
  const [navigation, setNavigation] = useState<NavigationState>({ level: 'root' });
  
  // 分类筛选（本地状态，不发网络请求）
  const [localCategory, setLocalCategory] = useState<ProductCategory | 'all'>('all');
  const [localSearch, setLocalSearch] = useState('');

  // 拖拽状态
  const [draggedProduct, setDraggedProduct] = useState<Product | null>(null);
  const [dropTargetSeries, setDropTargetSeries] = useState<string | null>(null);

  // 右键菜单状态
  const [contextMenu, setContextMenu] = useState<{
    type: 'series' | 'product';
    item: ProductSeries | Product;
    x: number;
    y: number;
  } | null>(null);

  // ===== 数据加载（只在挂载时执行一次）=====
  const fetchSeriesList = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('ditu_product_series')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      setSeriesList(data || []);
    } catch (error) {
      console.error('Error fetching series:', error);
    }
  }, []);

  // 初始化数据（只执行一次）
  useEffect(() => {
    fetchProducts();
    fetchCategoryStats();
    fetchSeriesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 首次加载完成标记
  useEffect(() => {
    if (!isLoading && products.length > 0 && isFirstLoad.current) {
      isFirstLoad.current = false;
    }
  }, [isLoading, products.length]);

  // 搜索防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      setLocalSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ===== 事件处理函数 =====

  // 分类切换 - 使用 startTransition 让 UI 保持响应
  // 这是 React 18 的关键优化：将列表重渲染标记为"非紧急"更新
  const handleCategoryChange = useCallback((category: ProductCategory | 'all') => {
    // 将整个状态更新包在 transition 里，让 UI 保持响应
    startTransition(() => {
      setLocalCategory(category);
      setSelectedSeries('all');
    });
  }, [startTransition]);

  // 排序
  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  }, [sortField]);

  // 复制产品
  const handleCopyProduct = useCallback(async (product: Product) => {
    try {
      await copyProduct(product);
      setContextMenu(null);
    } catch (error) {
      console.error('复制产品失败:', error);
      alert('复制产品失败');
    }
  }, [copyProduct]);

  // 删除产品
  const handleDeleteProduct = useCallback((product: Product) => {
    setConfirmDialog({
      title: '删除产品',
      message: `确定要删除产品 "${product.name}" 吗？此操作不可撤销。`,
      onConfirm: async () => {
        try {
          await deleteProduct(product.id);
          if (navigation.product?.id === product.id) {
            setNavigation(prev => ({ ...prev, product: undefined, level: 'series' }));
          }
        } catch (error) {
          console.error('Error deleting product:', error);
          alert('删除失败');
        }
        setConfirmDialog(null);
      },
    });
    setContextMenu(null);
  }, [deleteProduct, navigation.product]);

  // 删除型号
  const handleDeleteModel = useCallback((model: ProductModel) => {
    setConfirmDialog({
      title: '删除型号',
      message: `确定要删除型号 "${model.model_name}" 吗？`,
      onConfirm: async () => {
        try {
          await deleteProductModel(model.id);
          fetchProducts();
        } catch (error) {
          console.error('Error deleting model:', error);
          alert('删除失败');
        }
        setConfirmDialog(null);
      },
    });
  }, [deleteProductModel, fetchProducts]);

  // ===== 稳定化的回调函数（用于 memo 优化的子组件）=====
  
  // ProductCard 列表视图专用回调
  const handleCardToggleExpand = useCallback((productId: string) => {
    setExpandedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }, []);
  
  const handleCardEdit = useCallback((product: Product) => {
    setEditingProduct(product);
  }, []);
  
  const handleCardDelete = useCallback((product: Product) => {
    handleDeleteProduct(product);
  }, [handleDeleteProduct]);
  
  const handleCardCopy = useCallback((product: Product) => {
    handleCopyProduct(product);
  }, [handleCopyProduct]);
  
  const handleCardAddModel = useCallback((productId: string) => {
    setEditingModel({ productId, model: null });
  }, []);
  
  const handleCardEditModel = useCallback((productId: string, model: ProductModel) => {
    setEditingModel({ productId, model });
  }, []);
  
  const handleCardDeleteModel = useCallback((productId: string, modelId: string) => {
    // 需要从 products 中找到对应的 model
    const product = products.find(p => p.id === productId);
    const model = product?.models?.find(m => m.id === modelId);
    if (model) handleDeleteModel(model);
  }, [products, handleDeleteModel]);
  
  // ProductGridCard 网格视图专用回调
  const handleGridCardViewDetail = useCallback((product: Product) => {
    setDetailProduct(product);
  }, []);

  // ===== 层级导航方法 =====
  const navigateToCategory = useCallback((category: ProductCategory) => {
    const categorySeries = seriesList.filter(s => s.category === category);
    const firstSeries = categorySeries[0];
    
    if (firstSeries) {
      const seriesProducts = products.filter(p => p.series_name === firstSeries.name);
      const firstProduct = seriesProducts[0];
      
      if (firstProduct) {
        setNavigation({ level: 'product', category, series: firstSeries, product: firstProduct });
      } else {
        setNavigation({ level: 'series', category, series: firstSeries });
      }
    } else {
      setNavigation({ level: 'category', category });
    }
  }, [seriesList, products]);

  const navigateToSeries = useCallback((series: ProductSeries) => {
    const seriesProducts = products.filter(p => p.series_name === series.name);
    const firstProduct = seriesProducts[0];
    
    if (firstProduct) {
      setNavigation({ level: 'product', category: series.category as ProductCategory, series, product: firstProduct });
    } else {
      setNavigation({ level: 'series', category: series.category as ProductCategory, series });
    }
  }, [products]);

  const navigateToProduct = useCallback((product: Product) => {
    const series = seriesList.find(s => s.name === product.series_name);
    setNavigation({ level: 'product', category: product.category, series: series || undefined, product });
  }, [seriesList]);

  const navigateToRoot = useCallback(() => {
    setNavigation({ level: 'root' });
  }, []);

  // 切换到层级模式
  const handleSwitchToHierarchy = useCallback(() => {
    setPageMode('hierarchy');
    if (navigation.level === 'root') {
      navigateToCategory('all_in_one');
    }
  }, [navigation.level, navigateToCategory]);

  // ===== 拖拽处理 =====
  const handleDragStart = useCallback((e: React.DragEvent, product: Product) => {
    setDraggedProduct(product);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedProduct(null);
    setDropTargetSeries(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, seriesName: string) => {
    e.preventDefault();
    setDropTargetSeries(seriesName);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropTargetSeries(null);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, targetSeries: ProductSeries) => {
    e.preventDefault();
    setDropTargetSeries(null);
    
    if (!draggedProduct || draggedProduct.series_name === targetSeries.name) {
      setDraggedProduct(null);
      return;
    }

    try {
      await updateProduct(draggedProduct.id, {
        series_name: targetSeries.name,
        category: targetSeries.category as ProductCategory,
      });
      fetchProducts();
      fetchSeriesList();
    } catch (error) {
      console.error('Error moving product:', error);
    }
    
    setDraggedProduct(null);
  }, [draggedProduct, updateProduct, fetchProducts, fetchSeriesList]);

  // 右键菜单
  const openContextMenu = useCallback((e: React.MouseEvent, type: 'series' | 'product', item: ProductSeries | Product) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ type, item, x: e.clientX, y: e.clientY });
  }, []);

  // 点击关闭右键菜单
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [contextMenu]);

  // ===== 计算数据（使用 useMemo 缓存）=====
  
  // 筛选后的产品列表
  const sortedProducts = useMemo(() => {
    let result = [...products];
    
    // 分类筛选
    if (localCategory !== 'all') {
      result = result.filter(p => p.category === localCategory);
    }
    
    // 搜索筛选
    if (localSearch) {
      const searchLower = localSearch.toLowerCase();
      result = result.filter(p => 
        p.name?.toLowerCase().includes(searchLower) ||
        p.name_en?.toLowerCase().includes(searchLower) ||
        p.series_name?.toLowerCase().includes(searchLower) ||
        p.models?.some(m => m.model_name?.toLowerCase().includes(searchLower))
      );
    }
    
    // 系列筛选
    if (selectedSeries !== 'all') {
      const series = seriesList.find(s => s.id === selectedSeries);
      if (series) {
        result = result.filter(p => p.series_name === series.name);
      }
    }
    
    // 排序
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'created_at':
          comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          break;
        case 'models_count':
          comparison = (a.models?.length || 0) - (b.models?.length || 0);
          break;
        case 'category':
          comparison = (a.category || '').localeCompare(b.category || '');
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }, [products, localCategory, localSearch, selectedSeries, seriesList, sortField, sortOrder]);

  // 层级视图数据
  const currentSeriesList = useMemo(() => {
    if (navigation.category) {
      return seriesList.filter(s => s.category === navigation.category);
    }
    return seriesList;
  }, [seriesList, navigation.category]);

  const hierarchyProducts = useMemo(() => {
    let filtered = [...products];
    if (navigation.series) {
      filtered = filtered.filter(p => p.series_name === navigation.series?.name);
    } else if (navigation.category) {
      filtered = filtered.filter(p => p.category === navigation.category);
    }
    return filtered;
  }, [products, navigation.series, navigation.category]);

  const categorySeriesCount = useMemo(() => {
    const counts: Record<string, number> = { all_in_one: 0, product: 0, module: 0 };
    seriesList.forEach(s => {
      if (counts[s.category] !== undefined) {
        counts[s.category]++;
      }
    });
    return counts;
  }, [seriesList]);

  const seriesProductCount = useMemo(() => {
    const counts: Record<string, number> = {};
    seriesList.forEach(s => { counts[s.name] = 0; });
    products.forEach(p => {
      if (p.series_name && counts[p.series_name] !== undefined) {
        counts[p.series_name]++;
      }
    });
    return counts;
  }, [seriesList, products]);

  const filteredSeriesList = useMemo(() => {
    if (localCategory === 'all') return seriesList;
    return seriesList.filter(s => s.category === localCategory);
  }, [seriesList, localCategory]);

  // 层级视图标题
  const getHierarchyTitle = useCallback(() => {
    if (navigation.level === 'product' && navigation.product) {
      return `${navigation.product.name} - 型号管理`;
    }
    if (navigation.level === 'series' && navigation.series) {
      return `${navigation.series.name} - 产品列表`;
    }
    if (navigation.level === 'category' && navigation.category) {
      return `${ProductCategoryLabels[navigation.category]} - 系列列表`;
    }
    return '层级管理';
  }, [navigation]);

  // 500ms 延迟 loading
  const [showLoading, setShowLoading] = useState(false);
  useEffect(() => {
    if (!isLoading) {
      setShowLoading(false);
      return;
    }
    const timer = setTimeout(() => setShowLoading(true), 500);
    return () => clearTimeout(timer);
  }, [isLoading]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Grid3X3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {pageMode === 'products' ? '产品管理' : getHierarchyTitle()}
                </h1>
                <p className="text-sm text-gray-500">
                  共 {products.length} 个产品
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* 系列管理按钮 - 始终占位，只在层级模式下可点击 */}
              <button
                onClick={() => setShowSeriesManager(true)}
                disabled={pageMode !== 'hierarchy'}
                className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl transition-all ${
                  pageMode === 'hierarchy'
                    ? 'border-gray-200 text-gray-700 hover:bg-gray-50'
                    : 'border-transparent text-transparent cursor-default pointer-events-none'
                }`}
                style={{ visibility: pageMode === 'hierarchy' ? 'visible' : 'hidden' }}
              >
                <Settings2 className="w-4 h-4" />
                系列管理
              </button>

              {/* 视图切换按钮组 */}
              <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => { setPageMode('products'); navigateToRoot(); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    pageMode === 'products'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <List className="w-4 h-4" />
                  产品列表
                </button>
                <button
                  onClick={handleSwitchToHierarchy}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                    pageMode === 'hierarchy'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <GitBranch className="w-4 h-4" />
                  层级管理
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* 产品列表模式 */}
      {pageMode === 'products' && (
        <>
          <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
            <div className="max-w-7xl mx-auto px-4 py-3">
              <div className="flex items-center gap-4 flex-wrap">
                {/* 分类 Tabs */}
                <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                  {categoryOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleCategoryChange(option.value)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                        localCategory === option.value
                          ? 'bg-white text-blue-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {option.label}
                      <span className={`ml-1 text-xs ${
                        localCategory === option.value ? 'text-blue-500' : 'text-gray-400'
                      }`}>
                        ({categoryStats[option.value] || 0})
                      </span>
                    </button>
                  ))}
                </div>

                {/* 搜索 */}
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="搜索产品名称、型号..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 focus:bg-white transition-colors"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded"
                      >
                        <X className="w-3 h-3 text-gray-400" />
                      </button>
                    )}
                  </div>
                </div>

                {/* 排序 */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">排序:</span>
                  <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSort(option.value)}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all flex items-center gap-1 ${
                          sortField === option.value
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {option.label}
                        {sortField === option.value && (
                          sortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 视图切换 */}
                <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded transition-all ${
                      viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                    }`}
                    title="列表视图"
                  >
                    <List className={`w-4 h-4 ${viewMode === 'list' ? 'text-blue-600' : 'text-gray-500'}`} />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 rounded transition-all ${
                      viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                    }`}
                    title="网格视图"
                  >
                    <LayoutGrid className={`w-4 h-4 ${viewMode === 'grid' ? 'text-blue-600' : 'text-gray-500'}`} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 产品列表 */}
          <div className="max-w-7xl mx-auto px-4 py-6">
            {showLoading && isFirstLoad.current ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : sortedProducts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <Package className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">
                  {localSearch ? `未找到 "${localSearch}" 相关产品` : '暂无产品数据'}
                </p>
                <button
                  onClick={() => { setSearchQuery(''); setShowCreateModal(true); }}
                  className="mt-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  添加第一个产品
                </button>
              </div>
            ) : viewMode === 'list' ? (
              <div className={`space-y-3 transition-opacity duration-150 ${isPending ? 'opacity-60' : 'opacity-100'}`}>
                {sortedProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    isExpanded={expandedProducts.has(product.id)}
                    onToggleExpand={handleCardToggleExpand}
                    onEdit={handleCardEdit}
                    onDelete={handleCardDelete}
                    onCopy={handleCardCopy}
                    onAddModel={handleCardAddModel}
                    onEditModel={handleCardEditModel}
                    onDeleteModel={handleCardDeleteModel}
                  />
                ))}
              </div>
            ) : (
              <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 transition-opacity duration-150 ${isPending ? 'opacity-60' : 'opacity-100'}`}>
                {sortedProducts.map((product) => (
                  <ProductGridCard
                    key={product.id}
                    product={product}
                    onViewDetail={handleGridCardViewDetail}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* 层级管理模式 */}
      {pageMode === 'hierarchy' && (
        <HierarchyView
          navigation={navigation}
          currentSeriesList={currentSeriesList}
          currentProducts={hierarchyProducts}
          categorySeriesCount={categorySeriesCount}
          seriesProductCount={seriesProductCount}
          draggedProduct={draggedProduct}
          dropTargetSeries={dropTargetSeries}
          onNavigateToCategory={navigateToCategory}
          onNavigateToSeries={navigateToSeries}
          onNavigateToProduct={navigateToProduct}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onContextMenu={openContextMenu}
          onCreateSeries={() => setShowSeriesManager(true)}
          onCreateProduct={() => setShowCreateModal(true)}
          onEditProduct={setEditingProduct}
          onAddModel={(productId) => setEditingModel({ productId, model: null })}
          onEditModel={(productId, model) => setEditingModel({ productId, model })}
          onDeleteModel={handleDeleteModel}
        />
      )}

      {/* 右键菜单 */}
      {contextMenu && (
        <div
          className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 min-w-[140px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {contextMenu.type === 'series' && (
            <>
              <button
                onClick={() => { setShowSeriesManager(true); setContextMenu(null); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <Edit2 className="w-4 h-4" />
                编辑系列
              </button>
            </>
          )}
          {contextMenu.type === 'product' && (
            <>
              <button
                onClick={() => { setEditingProduct(contextMenu.item as Product); setContextMenu(null); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <Edit2 className="w-4 h-4" />
                编辑产品
              </button>
              <button
                onClick={() => handleCopyProduct(contextMenu.item as Product)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                复制产品
              </button>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => handleDeleteProduct(contextMenu.item as Product)}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                删除产品
              </button>
            </>
          )}
        </div>
      )}

      {/* 确认弹窗 */}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      {/* Modals */}
      {(showCreateModal || editingProduct) && (
        <ProductFormModal
          product={editingProduct}
          seriesList={seriesList}
          defaultCategory={pageMode === 'hierarchy' ? navigation.category : (localCategory !== 'all' ? localCategory : undefined)}
          defaultSeries={pageMode === 'hierarchy' ? navigation.series?.name : undefined}
          onClose={() => { setShowCreateModal(false); setEditingProduct(null); }}
          onSave={async (data) => {
            if (editingProduct) {
              await updateProduct(editingProduct.id, data);
            } else {
              await createProduct(data);
            }
            setShowCreateModal(false);
            setEditingProduct(null);
            fetchProducts();
          }}
        />
      )}

      {editingModel && (
        <ModelFormModal
          model={editingModel.model}
          productId={editingModel.productId}
          onClose={() => setEditingModel(null)}
          onSave={async (data) => {
            if (editingModel.model) {
              await updateProductModel(editingModel.model.id, data);
            } else {
              await createProductModel({ ...data, product_id: editingModel.productId });
            }
            setEditingModel(null);
            fetchProducts();
          }}
        />
      )}

      {showSeriesManager && (
        <SeriesManagerModal
          seriesList={seriesList}
          defaultCategory={pageMode === 'hierarchy' ? navigation.category : (localCategory !== 'all' ? localCategory : undefined)}
          onClose={() => setShowSeriesManager(false)}
          onRefresh={fetchSeriesList}
        />
      )}

      {detailProduct && (
        <ProductDetailPanel
          product={detailProduct}
          onClose={() => { setDetailProduct(null); setPanelEditingModel(null); }}
          onEditProduct={() => { setEditingProduct(detailProduct); setDetailProduct(null); }}
          onCopyProduct={() => { handleCopyProduct(detailProduct); setDetailProduct(null); }}
          onDeleteProduct={() => { handleDeleteProduct(detailProduct); setDetailProduct(null); }}
          editingModel={panelEditingModel}
          setEditingModel={setPanelEditingModel}
          onSaveModel={async (data, isNew) => {
            if (isNew) {
              await createProductModel({ ...data, product_id: detailProduct.id });
            } else if (panelEditingModel && typeof panelEditingModel === 'object') {
              await updateProductModel(panelEditingModel.id, data);
            }
            await fetchProducts();
            const updated = products.find(p => p.id === detailProduct.id);
            if (updated) setDetailProduct(updated);
          }}
          onDeleteModel={handleDeleteModel}
        />
      )}
    </div>
  );
}

export default ProductManagePage;
